export const DTRM_ID_ATTR = "data-dtrm-id";

const COLLAPSE_MS = 380;
const STAGGER_MS = 28;

type CursorPoint = { x: number; y: number };

let liveCursor: CursorPoint = { x: 0, y: 0 };
const activeChips = new Set<HTMLElement>();
let rafId: number | null = null;
let dragOverBound = false;

export function setCollapseCursorTarget(x: number, y: number) {
  liveCursor = { x, y };
}

function onDocumentDragOver(event: DragEvent) {
  setCollapseCursorTarget(event.clientX, event.clientY);
}

function bindCursorTracking() {
  if (dragOverBound) {
    return;
  }
  document.addEventListener("dragover", onDocumentDragOver, {
    passive: true,
  });
  dragOverBound = true;
}

function unbindCursorTracking() {
  if (!dragOverBound) {
    return;
  }
  document.removeEventListener("dragover", onDocumentDragOver);
  dragOverBound = false;
}

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function createFlyingChip(row: HTMLElement): HTMLElement | null {
  const nameRow = row.querySelector(
    ".flex.items-center.gap-x-2.relative",
  ) as HTMLElement | null;
  if (!nameRow) {
    return null;
  }

  const chip = document.createElement("div");
  chip.style.position = "fixed";
  chip.style.zIndex = "10000";
  chip.style.pointerEvents = "none";
  chip.style.display = "flex";
  chip.style.alignItems = "center";
  chip.style.gap = "8px";
  chip.style.padding = "8px 12px";
  chip.style.background = "white";
  chip.style.border = "1px solid #d1d5db";
  chip.style.borderRadius = "8px";
  chip.style.boxShadow = "0 8px 24px rgba(0,0,0,0.14)";
  chip.style.maxWidth = "200px";
  chip.style.willChange = "left, top, transform, opacity";
  chip.style.transformOrigin = "center center";

  const sourceIcon =
    nameRow.querySelector(":scope > svg, :scope > img") ??
    nameRow.firstElementChild?.querySelector("svg, img") ??
    null;
  if (sourceIcon) {
    const clonedIcon = sourceIcon.cloneNode(true) as HTMLElement;
    clonedIcon.setAttribute("width", "18");
    clonedIcon.setAttribute("height", "18");
    clonedIcon.style.width = "18px";
    clonedIcon.style.height = "18px";
    clonedIcon.style.flexShrink = "0";
    chip.appendChild(clonedIcon);
  }

  const nameEl = nameRow.querySelector("p");
  const label = document.createElement("span");
  label.textContent =
    nameEl?.textContent?.trim() || nameEl?.getAttribute("title") || "";
  label.style.fontSize = "13px";
  label.style.fontWeight = "600";
  label.style.color = "#111827";
  label.style.overflow = "hidden";
  label.style.textOverflow = "ellipsis";
  label.style.whiteSpace = "nowrap";
  chip.appendChild(label);

  return chip;
}

type FlyingChipState = {
  el: HTMLElement;
  startX: number;
  startY: number;
  halfWidth: number;
  halfHeight: number;
  startTime: number;
  duration: number;
};

let flying: FlyingChipState[] = [];

function tick(now: number) {
  const next: FlyingChipState[] = [];

  for (const chip of flying) {
    const elapsed = now - chip.startTime;
    if (elapsed < 0) {
      next.push(chip);
      continue;
    }

    const t = Math.min(1, elapsed / chip.duration);
    const eased = easeInOutCubic(t);

    const x = chip.startX + (liveCursor.x - chip.startX) * eased;
    const y = chip.startY + (liveCursor.y - chip.startY) * eased;
    const scale = 1 - 0.85 * eased;
    const opacity = 1 - eased;

    chip.el.style.left = `${x - chip.halfWidth}px`;
    chip.el.style.top = `${y - chip.halfHeight}px`;
    chip.el.style.transform = `scale(${scale})`;
    chip.el.style.opacity = String(opacity);

    if (t < 1) {
      next.push(chip);
    } else {
      activeChips.delete(chip.el);
      chip.el.remove();
    }
  }

  flying = next;

  if (flying.length > 0) {
    rafId = requestAnimationFrame(tick);
  } else {
    rafId = null;
    unbindCursorTracking();
  }
}

export function clearCollapseIntoDragAnimation() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  flying = [];
  activeChips.forEach((el) => el.remove());
  activeChips.clear();
  unbindCursorTracking();
}

/**
 * Drive-style collapse: flying chips home toward the live cursor
 * (updated via setCollapseCursorTarget) while list rows stay visible.
 */
export function playCollapseIntoDragAnimation(args: {
  itemIds: string[];
  cursor: CursorPoint;
}) {
  clearCollapseIntoDragAnimation();
  setCollapseCursorTarget(args.cursor.x, args.cursor.y);
  bindCursorTracking();

  if (args.itemIds.length <= 1) {
    unbindCursorTracking();
    return;
  }

  const rows = args.itemIds
    .map((id) => {
      const row = document.querySelector(
        `[${DTRM_ID_ATTR}="${CSS.escape(id)}"]`,
      ) as HTMLElement | null;
      if (!row) {
        return null;
      }
      const rect = row.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const distance = Math.hypot(cx - args.cursor.x, cy - args.cursor.y);
      return { id, row, rect, cx, cy, distance };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .sort((a, b) => a.distance - b.distance);

  const now = performance.now();

  rows.forEach((entry, index) => {
    const chip = createFlyingChip(entry.row);
    if (!chip) {
      return;
    }

    const chipWidth = Math.min(entry.rect.width * 0.45, 200);
    const halfWidth = chipWidth / 2;
    const halfHeight = 18;

    chip.style.width = `${chipWidth}px`;
    chip.style.left = `${entry.cx - halfWidth}px`;
    chip.style.top = `${entry.cy - halfHeight}px`;
    document.body.appendChild(chip);
    activeChips.add(chip);

    flying.push({
      el: chip,
      startX: entry.cx,
      startY: entry.cy,
      halfWidth,
      halfHeight,
      startTime: now + Math.min(index * STAGGER_MS, 120),
      duration: COLLAPSE_MS,
    });
  });

  if (flying.length > 0) {
    rafId = requestAnimationFrame(tick);
  }
}
